// apps/web/src/pages/jobs/CreateJob.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { trpc } from '@/trpc/client';
import { toast } from 'sonner';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useDropzone } from 'react-dropzone';
import imageCompression from 'browser-image-compression';
import { useAuthStore } from '@/store/authStore';
import { CreateJobSchema, TCreateJobSchema } from '@/lib/validators/job';
import { MALAWI_DISTRICTS, CARGO_TYPES, WEIGHT_UNITS } from '@/lib/constants';

import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Loader2, DollarSign, MapPin, Package, UploadCloud, X, Send } from 'lucide-react';

const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';

type PreviewFile = File & { preview: string };

const ImageUploader: React.FC<{
  files: PreviewFile[];
  setFiles: React.Dispatch<React.SetStateAction<PreviewFile[]>>;
}> = ({ files, setFiles }) => {
  const onDrop = useCallback(
    (accepted: File[]) => {
      if (files.length + accepted.length > 5) {
        return toast.error('Maximum of 5 images allowed.');
      }
      const withPreview = accepted.map((f) => Object.assign(f, { preview: URL.createObjectURL(f) })) as PreviewFile[];
      setFiles((p) => [...p, ...withPreview]);
    },
    [files, setFiles]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
  });

  const remove = (i: number) => {
    setFiles((p) => {
      const copy = [...p];
      const toRemove = copy[i];
      if (toRemove) URL.revokeObjectURL(toRemove.preview);
      copy.splice(i, 1);
      return copy;
    });
  };

  useEffect(() => {
    return () => {
      files.forEach((f) => URL.revokeObjectURL(f.preview));
    };
  }, [files]);

  return (
    <div>
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          isDragActive ? 'border-primary bg-primary/10' : 'hover:border-primary/50'
        }`}
      >
        <input {...getInputProps()} />
        <UploadCloud className="w-8 h-8 mx-auto text-muted-foreground" />
        <p className="font-semibold">
          {isDragActive ? 'Drop images here...' : 'Drag & drop or click to select'}
        </p>
        <p className="text-xs text-muted-foreground">Attach photos of your cargo</p>
      </div>

      {files.length > 0 && (
        <div className="mt-4 grid grid-cols-3 md:grid-cols-5 gap-2">
          {files.map((file, i) => (
            <div key={i} className="relative aspect-square">
              <img src={file.preview} alt="preview" className="w-full h-full object-cover rounded-md" />
              <Button
                type="button"
                variant="destructive"
                size="icon"
                className="absolute top-1 right-1 h-5 w-5"
                onClick={() => remove(i)}
              >
                <X size={10} />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CreateJob: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const [files, setFiles] = useState<PreviewFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const form = useForm<TCreateJobSchema>({
    resolver: zodResolver(CreateJobSchema),
    defaultValues: {
      title: '',
      description: '',
      pickupDistrict: '',
      pickupAddress: '',
      pickupContactName: '',
      pickupContactPhone: '',
      deliveryDistrict: '',
      deliveryAddress: '',
      deliveryContactName: '',
      deliveryContactPhone: '',
      cargoType: 'General Cargo',
      specialRequirements: '',
      loadCount: 1,
      cargoWeight: undefined,
      cargoUnit: undefined,
      priceAmount: undefined,
      preferredPickupDate: '',
      preferredDeliveryDate: '',
    },
  });

  const createJob = trpc.job.create.useMutation({
    onSuccess: (data) => {
      toast.success(`Job "${data.title}" posted successfully and is now ACTIVE.`);
      navigate('/dashboard');
    },
    onError: (err: any) => toast.error(err.message || 'Failed to create job'),
  });

  const compressAll = async (input: File[]): Promise<File[]> => {
    return Promise.all(input.map((f) => imageCompression(f, { maxSizeMB: 1, maxWidthOrHeight: 1080 })));
  };

  const onSubmit = async (data: TCreateJobSchema) => {
    let imagePaths: string[] = [];

    if (files.length > 0) {
      setIsUploading(true);
      try {
        const compressed = await compressAll(files);
        const uploadForm = new FormData();
        compressed.forEach((f) => uploadForm.append('files', f, f.name));
        uploadForm.append('type', 'jobs');

        const res = await fetch(`${apiUrl}/upload`, {
          method: 'POST',
          headers: { Authorization: token ? `Bearer ${token}` : '' },
          body: uploadForm,
        });

        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || 'Upload failed');
        }
        const json = (await res.json()) as { files: { filePath: string; mime: string }[] };
        imagePaths = json.files.map((f) => f.filePath);
      } catch (err: any) {
        setIsUploading(false);
        return toast.error(err.message || 'Image upload failed');
      } finally {
        setIsUploading(false);
      }
    }

    // Submit the job
    createJob.mutate({
      ...data,
      preferredPickupDate: data.preferredPickupDate ? new Date(data.preferredPickupDate).toISOString() : undefined,
      preferredDeliveryDate: data.preferredDeliveryDate ? new Date(data.preferredDeliveryDate).toISOString() : undefined,
      imagePaths,
    });
  };

  const isLoading = isUploading || createJob.isLoading;

  return (
    <div className="container mx-auto py-4 px-2 sm:py-12 sm:px-4 animate-fade-in">
      <Card className="max-w-4xl mx-auto glass">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-xl sm:text-3xl font-bold">Post a New Job</CardTitle>
          <CardDescription className="text-xs sm:text-sm">Your job will go ACTIVE immediately after creation.</CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
              {/* Title */}
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Job Title *</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., Urgent Delivery of Furniture" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Description */}
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Provide a detailed description of the cargo and job requirements."
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Pickup */}
              <Card className="bg-muted/50 p-6">
                <CardHeader className="p-0 mb-4">
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="text-primary" />
                    Pickup Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0 grid md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="pickupDistrict"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>District *</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select pickup district..." />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {MALAWI_DISTRICTS.map((d) => (
                              <SelectItem value={d} key={d}>
                                {d}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="pickupAddress"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Address / Area *</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g., Area 47, Street Name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="pickupContactName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Contact Name</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g., John Phiri" {...field} value={field.value ?? ''} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="pickupContactPhone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Contact Phone</FormLabel>
                        <FormControl>
                          <Input type="tel" placeholder="e.g., 0888123456" {...field} value={field.value ?? ''} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* Delivery */}
              <Card className="bg-muted/50 p-6">
                <CardHeader className="p-0 mb-4">
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="text-accent" />
                    Delivery Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0 grid md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="deliveryDistrict"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>District *</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select delivery district..." />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {MALAWI_DISTRICTS.map((d) => (
                              <SelectItem value={d} key={d}>
                                {d}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="deliveryAddress"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Address / Area *</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g., Mzuzu University Campus" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="deliveryContactName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Contact Name</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g., Jane Banda" {...field} value={field.value ?? ''} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="deliveryContactPhone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Contact Phone</FormLabel>
                        <FormControl>
                          <Input type="tel" placeholder="e.g., 0999123456" {...field} value={field.value ?? ''} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* Cargo */}
              <Card className="bg-muted/50 p-6">
                <CardHeader className="p-0 mb-4">
                  <CardTitle className="flex items-center gap-2">
                    <Package />
                    Cargo Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0 space-y-6">
                  <div className="grid md:grid-cols-2 gap-6 items-start">
                    <FormField
                      control={form.control}
                      name="cargoType"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Cargo Type</FormLabel>
                          <FormControl>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <SelectTrigger>
                                <SelectValue placeholder="Select..." />
                              </SelectTrigger>
                              <SelectContent>
                                {CARGO_TYPES.map((t) => (
                                  <SelectItem value={t} key={t}>
                                    {t}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="flex gap-2">
                      <FormField
                        control={form.control}
                        name="cargoWeight"
                        render={({ field }) => (
                          <FormItem className="flex-grow">
                            <FormLabel>Approx. Weight</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                placeholder="e.g., 50"
                                {...field}
                                value={field.value ?? ''}
                                onChange={(e) =>
                                  field.onChange(e.target.value === '' ? undefined : e.currentTarget.valueAsNumber)
                                }
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="cargoUnit"
                        render={({ field }) => (
                          <FormItem className="w-1/3">
                            <FormLabel>Unit</FormLabel>
                            <FormControl>
                              <Select onValueChange={field.onChange} defaultValue={field.value}>
                                <SelectTrigger>
                                  <SelectValue placeholder="Unit" />
                                </SelectTrigger>
                                <SelectContent>
                                  {WEIGHT_UNITS.map((u) => (
                                    <SelectItem value={u.value} key={u.value}>
                                      {u.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  <FormField
                    control={form.control}
                    name="specialRequirements"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Special Requirements</FormLabel>
                        <FormControl>
                          <Textarea placeholder="e.g., Fragile items..." {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="space-y-2">
                    <FormLabel>Cargo Images</FormLabel>
                    <ImageUploader files={files} setFiles={setFiles} />
                  </div>
                </CardContent>
              </Card>

              {/* Price & Loads */}
              <div className="grid lg:grid-cols-2 gap-6 items-start">
                <FormField
                  control={form.control}
                  name="priceAmount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Offer Price (MWK) *</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                          <Input
                            type="number"
                            className="pl-10"
                            placeholder="e.g., 150000"
                            {...field}
                            value={field.value ?? ''}
                            onChange={(e) =>
                              field.onChange(e.target.value === '' ? undefined : e.currentTarget.valueAsNumber)
                            }
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="loadCount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Loads *</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="e.g., 1"
                          {...field}
                          value={field.value ?? 1}
                          onChange={(e) => {
                            const val = e.currentTarget.valueAsNumber;
                            field.onChange(isNaN(val) || val < 1 ? 1 : val);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Dates */}
              <div className="grid md:grid-cols-2 gap-6 items-start">
                <FormField
                  control={form.control}
                  name="preferredPickupDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Pickup Date</FormLabel>
                      <FormControl>
                        <Input
                          type="datetime-local"
                          {...field}
                          value={field.value || ''}
                          onChange={(e) => field.onChange(e.target.value || '')}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="preferredDeliveryDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Delivery Date</FormLabel>
                      <FormControl>
                        <Input
                          type="datetime-local"
                          {...field}
                          value={field.value || ''}
                          onChange={(e) => field.onChange(e.target.value || '')}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex justify-end pt-4 border-t">
                <Button type="submit" size="lg" variant="premium" disabled={isLoading}>
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {isUploading ? 'Processing...' : 'Publish Job'}
                  <Send className="ml-2" />
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateJob;
